import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  addDoc,
  Timestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db, auth } from '@/config/firebase';
import { UserProfile, WorkoutSession } from '@/types/workout';

/**
 * Safely convert a date value to ISO string
 * Handles Date objects, null, undefined, and other values
 */
 
function safeToISOString(dateValue: any): string | null {
  if (!dateValue) return null;
  if (dateValue instanceof Date) {
    return dateValue.toISOString();
  }
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return null;
    return date.toISOString();
  } catch {
    return null;
  }
}

/**
 * User Profile Operations
 */
export async function createUserProfile(userId: string, profile: Omit<UserProfile, 'uid'>): Promise<void> {
  const userRef = doc(db, 'users', userId);

  // Convert dates to Firestore Timestamps safely
  const profileData: any = {
    ...profile,
    uid: userId,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  // Ensure all required fields are present
  // Handle startDate conversion
  if (profile.startDate) {
    try {
      const startDate = profile.startDate instanceof Date ? profile.startDate : new Date(profile.startDate);
      if (isNaN(startDate.getTime())) {
        throw new Error('Invalid start date provided');
      }
      profileData.startDate = Timestamp.fromDate(startDate);
    } catch (err) {
      throw new Error('Invalid start date format');
    }
  }

  // Handle competitionDate conversion
  if (profile.competitionDate !== undefined) {
    if (profile.competitionDate === null || (typeof profile.competitionDate === 'string' && profile.competitionDate === '')) {
      profileData.competitionDate = null;
    } else {
      try {
        const competitionDate = profile.competitionDate instanceof Date ? profile.competitionDate : new Date(profile.competitionDate);
        if (isNaN(competitionDate.getTime())) {
          // Invalid competition date, setting to null
          profileData.competitionDate = null;
        } else {
          profileData.competitionDate = Timestamp.fromDate(competitionDate);
        }
      } catch (err) {
        // Failed to parse competition date, setting to null
        profileData.competitionDate = null;
      }
    }
  } else {
    profileData.competitionDate = null;
  }

  await setDoc(userRef, profileData);
}

export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    const data = userSnap.data();
    try {
      const profile = {
        ...data,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
        startDate: data.startDate.toDate(),
        competitionDate: data.competitionDate && typeof data.competitionDate.toDate === 'function'
          ? data.competitionDate.toDate()
          : null,
      } as UserProfile;

      // Profile retrieved successfully
      return profile;
    } catch (error) {
      // Failed to parse user profile data
      return null;
    }
  }

  return null;
}

/**
 * Debug function to manually check if profile exists
 */
export async function debugCheckProfile(userId: string): Promise<void> {
  try {
    const _profile = await getUserProfile(userId);

    if (_profile) {
      // Profile found and processed successfully
    }
  } catch (_error) {
    // Debug profile check failed
  }
}

/**
 * Global debug function for browser console
 */
declare global {
   
  interface Window {
    debugHyroxProfile: () => Promise<void>;
  }
}

// Make debug function available globally
if (typeof window !== 'undefined') {
  window.debugHyroxProfile = async () => {
    const user = auth.currentUser;
    if (user) {
      await debugCheckProfile(user.uid);
      // Also check raw Firestore data
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      // Raw Firestore data checked
      if (userSnap.exists()) {
        // const _data = userSnap.data();
        // Raw user data available for debugging
      }
    }
  };
}

export async function updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<void> {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const userRef = doc(db, 'users', userId);

    // Convert dates to Firestore Timestamps safely
    const updateData: any = {
      updatedAt: Timestamp.now(),
    };

    // Only include fields that are not undefined
    Object.keys(updates).forEach(key => {
      if (updates[key as keyof UserProfile] !== undefined) {
        updateData[key] = updates[key as keyof UserProfile];
      }
    });

    // Handle startDate conversion
    if (updates.startDate) {
      try {
        const startDate = updates.startDate instanceof Date ? updates.startDate : new Date(updates.startDate);
        if (isNaN(startDate.getTime())) {
          throw new Error('Invalid start date provided');
        }
        updateData.startDate = Timestamp.fromDate(startDate);
      } catch (err) {
        throw new Error('Invalid start date format');
      }
    }

    // Handle competitionDate conversion
    if (updates.competitionDate !== undefined) {
      if (updates.competitionDate === null || (typeof updates.competitionDate === 'string' && updates.competitionDate === '')) {
        // Explicitly set to null for empty/null values (22-week default program)
        updateData.competitionDate = null;
      } else {
        try {
          const competitionDate = updates.competitionDate instanceof Date ? updates.competitionDate : new Date(updates.competitionDate);
          if (isNaN(competitionDate.getTime())) {
            // Invalid competition date in update, setting to null
            updateData.competitionDate = null;
          } else {
            updateData.competitionDate = Timestamp.fromDate(competitionDate);
          }
        } catch (err) {
          // Failed to parse competition date in update, setting to null
          updateData.competitionDate = null;
        }
      }
    }

    // Check if document exists first
    const docSnap = await getDoc(userRef);
    if (!docSnap.exists()) {
      // Profile document does not exist for user
      throw new Error('Profile document does not exist. Please create profile first.');
    }

    // Filter out any undefined values before updating (Firestore doesn't allow undefined)
    const cleanUpdateData = Object.fromEntries(
      Object.entries(updateData).filter(([_, value]) => value !== undefined)
    );

    await updateDoc(userRef, cleanUpdateData);
  } catch (error: any) {
    // Failed to update user profile
    // Provide more specific error messages
    if (error.code === 'not-found') {
      throw new Error('User profile not found. Please try creating a new profile.');
    } else if (error.code === 'permission-denied') {
      throw new Error('Permission denied. Please check your authentication.');
    } else if (error.code === 'unavailable') {
      throw new Error('Service temporarily unavailable. Please try again later.');
    } else if (error.message?.includes('offline')) {
      throw new Error('You appear to be offline. Please check your connection.');
    } else if (error.message?.includes('Invalid time value') || error.message?.includes('Invalid date')) {
      throw new Error('Invalid date provided. Please check your date inputs.');
    }

    throw error;
  }
}

/**
 * Workout Session Operations
 */
export async function saveWorkoutSession(session: Omit<WorkoutSession, 'id'>): Promise<string> {
  const sessionsRef = collection(db, 'workouts', session.userId, 'sessions');
  const docRef = await addDoc(sessionsRef, {
    ...session,
    date: Timestamp.fromDate(session.date),
    completedAt: session.completedAt ? Timestamp.fromDate(session.completedAt) : null,
  });
  return docRef.id;
}

export async function getWorkoutSession(userId: string, sessionId: string): Promise<WorkoutSession | null> {
  const sessionRef = doc(db, 'workouts', userId, 'sessions', sessionId);
  const sessionSnap = await getDoc(sessionRef);
  
  if (sessionSnap.exists()) {
    const data = sessionSnap.data();
    return {
      ...data,
      id: sessionSnap.id,
      date: data.date.toDate(),
      completedAt: data.completedAt?.toDate(),
    } as WorkoutSession;
  }
  
  return null;
}

export async function getUserWorkoutSessions(
  userId: string,
  week?: number,
  limitCount: number = 50
): Promise<WorkoutSession[]> {
  const sessionsRef = collection(db, 'workouts', userId, 'sessions');
  let q = query(sessionsRef, orderBy('date', 'desc'));

  if (week) {
    q = query(sessionsRef, where('week', '==', week), orderBy('date', 'desc'));
  } else {
    q = query(sessionsRef, orderBy('date', 'desc'), limit(limitCount));
  }

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id,
      date: data.date.toDate(),
      completedAt: data.completedAt?.toDate(),
    } as WorkoutSession;
  });
}

/**
 * Real-time listeners
 */
export function subscribeToUserProfile(
  userId: string,
  callback: (_profile: UserProfile | null) => void
): () => void {
  const userRef = doc(db, 'users', userId);
  return onSnapshot(userRef, (doc) => {

    if (doc.exists()) {
      const data = doc.data();
      try {
        const userProfile = {
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
          startDate: data.startDate.toDate(),
          competitionDate: data.competitionDate && typeof data.competitionDate.toDate === 'function'
            ? data.competitionDate.toDate()
            : null,
        } as UserProfile;


        callback(userProfile);
      } catch (error) {
        // Failed to parse profile in subscription
        callback(null);
      }
    } else {

      callback(null);
    }
  }, (_error) => {
    // Profile subscription error - callback with null
    callback(null);
  });
}
